import { ReactNode, useMemo } from 'react';
import { Box, Button, Chip, Grid, GridProps, Stack, SxProps, Typography } from '@mui/material';
import { WrappedPatient } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/Patient';
import { capitalCase } from 'change-case';
import useLocales, {
  i18nFunction,
} from 'src/careflow/ph-core/internationalization/hook/useLocales';
import { spreadSxProp } from 'src/utils/cssStyles';
import useTenantConfigData, {
  BannerInfoName,
} from 'src/careflow/ph-core/multi-tenant/tenant/hook/useTenantConfigData';
import { PATH_DASHBOARD } from 'src/routes/paths';
import uuidv4 from 'src/utils/uuidv4';
import PatientTags from './PatientTags';
import useScores from '../detail/components/scores/useScores';
import IcomxInlineTelecomAction from 'src/sections/crs/patient/components/icomx/IcomxInlineTelecomAction';

type PatientBasicInfoProps = {
  patient: WrappedPatient;
  bannerConfigExternal?: BannerInfoComponents;
  showGoToProfile?: boolean;
  episodeOfCare?: string[];
  onRefresh?: () => void;
};

export type BannerInfoComponents = {
  [key in BannerInfoName]?: () => React.ReactNode;
};

export function PatientBasicInfo({
  patient,
  bannerConfigExternal,
  showGoToProfile = false,
  episodeOfCare,
  onRefresh,
}: PatientBasicInfoProps) {
  const { i18n } = useLocales();
  const { componentsData, configurations } = useTenantConfigData();
  const { sil } = useScores({ patientId: patient?.id, configurations: configurations?.fhir });
  const { showSILInformation, showMedicaID } = componentsData ?? {};

  const bannerInfoMemo = useMemo<BannerInfoComponents>(
    () => ({
      [BannerInfoName.MRN]: () => (
        <InfoComponent
          label={i18n('patients.mrn', 'crs')}
          value={patient.getMRN(configurations?.mpi?.systemMrn)?.value}
          key="bannedInfoNameMRN"
        />
      ),
      [BannerInfoName.AGE]: () => (
        <InfoComponent
          label={i18n('patients.age', 'crs')}
          value={`${patient.getAgeInYears()} years`}
          key="bannedInfoNameAge"
        />
      ),
      [BannerInfoName.GENDER]: () => (
        <InfoComponent
          label={i18n('patients.gender', 'crs')}
          value={patient.gender}
          valueSx={{ textTransform: 'capitalize' }}
          key="bannedInfoNameGender"
        />
      ),
      [BannerInfoName.PRIMARY_PHONE]: () => (
        <InfoComponent
          label={i18n('patients.phone', 'crs')}
          value={patient.getPrimaryPhone()?.value}
          endAdornment={
            <IcomxInlineTelecomAction
              resourceType="Patient"
              resourceId={patient?.id}
              system="phone"
              tooltip="Open patient phone in iComx"
              value={patient.getPrimaryPhone()?.value}
            />
          }
          key="bannedInfoNamePrimaryPhone"
        />
      ),
      [BannerInfoName.PRIMARY_EMAIL]: () => (
        <InfoComponent
          label={i18n('patients.email', 'crs')}
          value={patient.getPrimaryEmail()?.value}
          endAdornment={
            <IcomxInlineTelecomAction
              resourceType="Patient"
              resourceId={patient?.id}
              system="email"
              tooltip="Open patient email in iComx"
              value={patient.getPrimaryEmail()?.value}
            />
          }
          key="bannedInfoNamePrimaryEmail"
        />
      ),
      [BannerInfoName.MEDICAID]: () =>
        showMedicaID ? (
          <InfoComponent
            label={i18n('patients.medicaid', 'crs')}
            value={patient.getMedicaId(configurations?.fhir)}
            key="bannedInfoNameMedicaid"
          />
        ) : null,
      [BannerInfoName.PRIMARY_ZIPCODE]: () => (
        <InfoComponent
          label={i18n('patients.details.addressDetails.zipCode', 'crs')}
          value={patient.getZIPCode()}
          key="bannedInfoNamePrimaryZipCode"
        />
      ),
      [BannerInfoName.MANAGING_ORG]: () => (
        <InfoComponent
          label={i18n('patients.details.personalDetails.organization', 'crs')}
          value={patient.getOrganization()}
          key="bannedInfoNameOrganization"
        />
      ),
      [BannerInfoName.SIL_SCORE]: () =>
        showSILInformation ? (
          <InfoComponent
            label={i18n('patients.sil', 'crs')}
            value={String(sil?.getValue?.())}
            key="bannedInfoNameSilScope"
          />
        ) : null,
      [BannerInfoName.EOC]: () =>
        episodeOfCare ? <EOCBannerComponent episodeOfCare={episodeOfCare} i18n={i18n} /> : null,
    }),
    [patient, sil, showSILInformation, showMedicaID, configurations, episodeOfCare, i18n]
  );

  const bannerInfoComponents: BannerInfoComponents = bannerConfigExternal || bannerInfoMemo;

  return (
    <>
      <Stack direction="row" alignItems="center" sx={{ my: 1 }} spacing={2}>
        <Typography variant="h6" sx={{ textTransform: 'capitalize' }}>
          {`${patient?.name?.[0]?.given?.[0]} 
            ${patient?.name?.[0]?.given?.[1] !== undefined ? patient?.name?.[0]?.given?.[1] : ''} 
            ${patient?.name?.[0]?.family}`}
        </Typography>
        {showGoToProfile ? (
          <Button
            variant="contained"
            onClick={() => {
              if (!patient?.id) return;
              window.open(PATH_DASHBOARD.crs.patient.details.forId(patient.id), '_blank');
            }}
            size="small"
          >
            {i18n('patients.goToProfile', 'crs')}
          </Button>
        ) : (
          <Chip
            size="small"
            label={capitalCase(patient.getStatus())}
            color={(patient.getStatus() === 'active' && 'info') || 'error'}
          />
        )}
      </Stack>
      <Grid spacing={2} container>
        {Object.entries(bannerInfoComponents).map(([_, renderComponent]) => renderComponent())}
      </Grid>
      <PatientTags patient={patient} onRefresh={onRefresh} />
    </>
  );
}

export interface InfoComponentProps extends GridProps {
  label: string | null | undefined;
  value: string | null | undefined;
  containerSx?: SxProps;
  labelSx?: SxProps;
  valueSx?: SxProps;
  endAdornment?: ReactNode;
}

export const InfoComponent = ({
  label,
  value,
  containerSx,
  labelSx,
  valueSx,
  endAdornment,
  ...gridProps
}: InfoComponentProps) => (
  <Grid
    item
    sx={[{ display: 'flex', flexDirection: 'row' }, ...spreadSxProp(containerSx)]}
    {...gridProps}
  >
    <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
      <Typography
        variant="body2"
        sx={[{ color: 'text.secondary', mr: 1, whiteSpace: 'nowrap' }, ...spreadSxProp(labelSx)]}
      >
        {label ?? ''}:
      </Typography>
      <Typography variant="body2" sx={[{ whiteSpace: 'nowrap' }, ...spreadSxProp(valueSx)]}>
        {value ?? ''}
      </Typography>
      {endAdornment}
    </Box>
  </Grid>
);

export const EOCBannerComponent = ({
  episodeOfCare,
  i18n,
}: {
  episodeOfCare: string[];
  i18n: i18nFunction;
}) => (
  <Grid item sx={[{ display: 'flex', flexDirection: 'row' }]} spacing={2} key={uuidv4()}>
    <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
      <Typography variant="body2" sx={[{ color: 'text.secondary', mr: 1, whiteSpace: 'nowrap' }]}>
        {i18n('patients.details.personalDetails.eoc', 'crs')}:
      </Typography>
      {episodeOfCare?.map((eoc) => (
        <Chip key={eoc} size="small" label={eoc} color={'info'} />
      ))}
    </Box>
  </Grid>
);
